import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import StatusBadge from "@/components/base/StatusBadge";
import Pagination from "@/components/base/Pagination";
import { Skeleton } from "@/components/base/Loading";
import { usePagination } from "@/hooks/usePagination";
import { formatFullDate, formatRelativeTime } from "@/lib/format";
import { downloadCsv, type CsvColumn } from "@/lib/csv";
import { APPLICATION_STATUS_META, LICENSE_STATUS_META, editionLabel } from "@/lib/license";
import CustomerStatusModal from "@/pages/admin/customers/components/CustomerStatusModal";
import FollowUpModal from "@/pages/admin/customers/components/FollowUpModal";
import type { Customer, StaffMember } from "@/pages/admin/customers/hooks/useCustomers";

type Filter = "all" | "registered" | "guest" | "pending" | "risky" | "following" | "suspended";
type SortKey = "default" | "pending" | "licenses";

const PAGE_SIZE = 8;
// 「最近被单独补推过」标记的时效：与推送去重周期一致，30 天内单独补推过才高亮。
const SINGLE_PUSH_RECENT_DAYS = 30;

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "全部客户" },
  { key: "registered", label: "已注册" },
  { key: "guest", label: "游客申请" },
  { key: "pending", label: "待审批" },
  { key: "risky", label: "风险客户" },
  { key: "following", label: "跟进中" },
  { key: "suspended", label: "已停用" },
];

const SORTS: { key: SortKey; label: string }[] = [
  { key: "default", label: "默认排序" },
  { key: "pending", label: "按待审批申请数" },
  { key: "licenses", label: "按许可证数" },
];

const CUSTOMER_CSV_COLUMNS: CsvColumn<Customer>[] = [
  { key: "name", label: "客户名称", value: (row) => row.name },
  { key: "email", label: "邮箱", value: (row) => row.email ?? "" },
  { key: "company", label: "公司", value: (row) => row.company ?? "" },
  { key: "role", label: "角色", value: (row) => (row.role === "admin" ? "管理员" : "客户") },
  {
    key: "account",
    label: "账号类型",
    value: (row) => (!row.registered ? "游客申请" : row.status === "suspended" ? "已停用" : "已注册"),
  },
  { key: "applications", label: "申请总数", value: (row) => row.applications.length },
  { key: "pending", label: "待审批", value: (row) => row.pendingCount },
  { key: "licenses", label: "许可证数", value: (row) => row.licenses.length },
  { key: "active_licenses", label: "有效许可证", value: (row) => row.activeLicenseCount },
  { key: "seats", label: "授权席位", value: (row) => row.totalSeats },
  {
    key: "license_summary",
    label: "许可证汇总",
    value: (row) =>
      row.licenses.length === 0
        ? "无"
        : row.licenses
            .map(
              (license) =>
                `${license.product_name}（${editionLabel(license.edition)}·${
                  LICENSE_STATUS_META[license.status]?.label ?? license.status
                }·${license.activation_count}/${license.max_activations}）`,
            )
            .join("；"),
  },
  { key: "created_at", label: "注册时间", value: (row) => (row.createdAt ? formatFullDate(row.createdAt) : "") },
  { key: "last_activity", label: "最近一次操作", value: (row) => (row.lastActivity ? formatStamp(row.lastActivity) : "无记录") },
  { key: "devices", label: "设备总数", value: (row) => row.devices.length },
  { key: "risk", label: "风险标记", value: (row) => (row.riskFlags.length ? row.riskFlags.map((flag) => flag.label).join("、") : "正常") },
];

const RISK_CSV_COLUMNS: CsvColumn<Customer>[] = [
  { key: "name", label: "客户名称", value: (row) => row.name },
  { key: "email", label: "邮箱", value: (row) => row.email ?? "" },
  { key: "company", label: "公司", value: (row) => row.company ?? "" },
  {
    key: "account",
    label: "账号类型",
    value: (row) => (!row.registered ? "游客申请" : row.status === "suspended" ? "已停用" : "已注册"),
  },
  { key: "risks", label: "风险标记", value: (row) => row.riskFlags.map((flag) => flag.label).join("、") },
  { key: "risk_detail", label: "风险依据", value: (row) => row.riskFlags.map((flag) => flag.detail).join("；") },
  { key: "follow_up", label: "跟进状态", value: (row) => (row.followUp ? "跟进中" : "未跟进") },
  { key: "follow_up_note", label: "跟进备注", value: (row) => row.followUpNote ?? "" },
  { key: "last_activity", label: "最近一次操作", value: (row) => (row.lastActivity ? formatStamp(row.lastActivity) : "无记录") },
  { key: "devices", label: "设备总数", value: (row) => row.devices.length },
  { key: "released", label: "已解绑设备", value: (row) => row.releasedDeviceCount },
  { key: "licenses", label: "许可证数", value: (row) => row.licenses.length },
  { key: "pending", label: "待审批", value: (row) => row.pendingCount },
];

const FOLLOWUP_CSV_COLUMNS: CsvColumn<Customer>[] = [
  { key: "name", label: "客户名称", value: (row) => row.name },
  { key: "email", label: "邮箱", value: (row) => row.email ?? "" },
  { key: "company", label: "公司", value: (row) => row.company ?? "" },
  {
    key: "account",
    label: "账号类型",
    value: (row) => (!row.registered ? "游客申请" : row.status === "suspended" ? "已停用" : "已注册"),
  },
  { key: "risk", label: "风险标记", value: (row) => (row.riskFlags.length ? row.riskFlags.map((flag) => flag.label).join("、") : "正常") },
  { key: "risk_detail", label: "风险依据", value: (row) => row.riskFlags.map((flag) => flag.detail).join("；") },
  { key: "follow_up_note", label: "当前跟进备注", value: (row) => row.followUpNote ?? "" },
  { key: "assignee", label: "跟进负责人", value: (row) => row.followUpAssignee ?? "未分配" },
  { key: "followed_at", label: "加入跟进时间", value: (row) => (row.followedAt ? formatStamp(new Date(row.followedAt).getTime()) : "") },
  { key: "last_activity", label: "最近一次操作", value: (row) => (row.lastActivity ? formatStamp(row.lastActivity) : "无记录") },
  { key: "devices", label: "设备总数", value: (row) => row.devices.length },
  { key: "released", label: "已解绑设备", value: (row) => row.releasedDeviceCount },
  { key: "licenses", label: "许可证数", value: (row) => row.licenses.length },
  { key: "pending", label: "待审批", value: (row) => row.pendingCount },
];

function normalizeFilter(value: string | null): Filter {
  return FILTERS.some((item) => item.key === value) ? (value as Filter) : "all";
}

function formatStamp(ms: number): string {
  if (!ms) return "";
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function accountMeta(customer: Customer): { label: string; className: string } {
  if (customer.status === "suspended") return { label: "已停用", className: "bg-accent-100 text-accent-800" };
  if (!customer.registered) return { label: "游客申请", className: "bg-secondary-100 text-secondary-900" };
  return { label: "正常", className: "bg-primary-100 text-primary-700" };
}

interface CustomerListProps {
  customers: Customer[];
  staff: StaffMember[];
  loading: boolean;
  error: string | null;
  lastSinglePushAt: Record<string, string>;
  onRetry: () => void;
  onSetStatus: (userId: string, status: "active" | "suspended") => Promise<void>;
  onSetFollowUp: (userId: string, followUp: boolean, note: string, assignee: string) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

export default function CustomerList({ customers, staff, loading, error, lastSinglePushAt, onRetry, onSetStatus, onSetFollowUp, notify }: CustomerListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<Filter>(() => normalizeFilter(searchParams.get("filter")));
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState<SortKey>("default");
  const [assignee, setAssignee] = useState<string>(() => searchParams.get("assignee") ?? "all");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [target, setTarget] = useState<Customer | null>(null);
  const [followUpTarget, setFollowUpTarget] = useState<Customer | null>(null);
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exported, setExported] = useState(false);
  const [riskExported, setRiskExported] = useState(false);
  const [followUpExported, setFollowUpExported] = useState(false);

  const matchesFilter = (customer: Customer, key: Filter): boolean => {
    if (key === "all") return true;
    if (key === "registered") return customer.registered;
    if (key === "guest") return !customer.registered;
    if (key === "pending") return customer.pendingCount > 0;
    if (key === "risky") return customer.riskFlags.length > 0;
    if (key === "following") return customer.followUp;
    return customer.status === "suspended";
  };

  // 同步来自看板「查看清单」跳转的负责人筛选参数，以及健康度看板跳转的筛选标签。
  useEffect(() => {
    const nextAssignee = searchParams.get("assignee") ?? "all";
    setAssignee((prev) => (prev === nextAssignee ? prev : nextAssignee));
    const nextFilter = normalizeFilter(searchParams.get("filter"));
    setFilter((prev) => (prev === nextFilter ? prev : nextFilter));
  }, [searchParams]);

  const handleAssigneeChange = (value: string) => {
    setAssignee(value);
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("assignee");
    else next.set("assignee", value);
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const handleFilterChange = (key: Filter) => {
    setFilter(key);
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("filter");
    else next.set("filter", key);
    setSearchParams(next, { replace: true });
  };

  const countFor = (key: Filter) => customers.filter((customer) => matchesFilter(customer, key)).length;

  const assigneeOptions = useMemo(() => {
    const set = new Set<string>();
    staff.forEach((member) => set.add(member.name));
    customers.forEach((customer) => {
      if (customer.followUpAssignee) set.add(customer.followUpAssignee);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "zh-CN"));
  }, [customers, staff]);

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    const list = customers.filter((customer) => {
      if (!matchesFilter(customer, filter)) return false;
      if (assignee === "none") {
        if (!customer.followUp || customer.followUpAssignee) return false;
      } else if (assignee !== "all" && customer.followUpAssignee !== assignee) {
        return false;
      }
      if (!term) return true;
      return (
        customer.name.toLowerCase().includes(term) ||
        (customer.email ?? "").toLowerCase().includes(term) ||
        (customer.company ?? "").toLowerCase().includes(term)
      );
    });
    if (sort === "pending") {
      return [...list].sort(
        (a, b) => b.pendingCount - a.pendingCount || b.applications.length - a.applications.length,
      );
    }
    if (sort === "licenses") {
      return [...list].sort((a, b) => b.licenses.length - a.licenses.length || b.totalSeats - a.totalSeats);
    }
    return list;
  }, [customers, staff, filter, keyword, sort, assignee]);

  const { page, totalPages, total, pageSize, pagedItems, setPage } = usePagination(
    filtered,
    PAGE_SIZE,
    `${filter}|${keyword}|${sort}|${assignee}`,
  );

  const handleExport = () => {
    if (filtered.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`客户台账_${stamp}.csv`, CUSTOMER_CSV_COLUMNS, filtered);
    setExported(true);
    window.setTimeout(() => setExported(false), 2000);
  };

  const riskyCustomers = useMemo(
    () => customers.filter((customer) => customer.riskFlags.length > 0),
    [customers],
  );

  const handleRiskExport = () => {
    if (riskyCustomers.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`风险客户名单_${stamp}.csv`, RISK_CSV_COLUMNS, riskyCustomers);
    setRiskExported(true);
    window.setTimeout(() => setRiskExported(false), 2000);
  };

  const followedCustomers = useMemo(
    () => customers.filter((customer) => customer.followUp),
    [customers],
  );

  const assigneeWorkload = useMemo(() => {
    const map: Record<string, number> = {};
    followedCustomers.forEach((customer) => {
      const name = customer.followUpAssignee?.trim();
      if (!name) return;
      map[name] = (map[name] ?? 0) + 1;
    });
    return map;
  }, [followedCustomers]);

  const handleFollowUpExport = () => {
    if (followedCustomers.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`客服跟进清单_${stamp}.csv`, FOLLOWUP_CSV_COLUMNS, followedCustomers);
    setFollowUpExported(true);
    window.setTimeout(() => setFollowUpExported(false), 2000);
  };

  const confirmStatus = async () => {
    if (!target?.userId) return;
    const next = target.status === "suspended" ? "active" : "suspended";
    setBusy(true);
    try {
      await onSetStatus(target.userId, next);
      notify(
        next === "suspended" ? `已停用「${target.name}」的账号` : `已恢复「${target.name}」的账号`,
        "success",
      );
      setTarget(null);
    } catch (err) {
      notify(err instanceof Error ? err.message : "操作失败，请稍后重试", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleFollowUpSave = async (followUp: boolean, note: string, assigneeValue: string) => {
    if (!followUpTarget?.userId) return;
    setFollowUpBusy(true);
    try {
      await onSetFollowUp(followUpTarget.userId, followUp, note, assigneeValue);
      notify(
        followUp
          ? `已将「${followUpTarget.name}」加入跟进清单`
          : `已将「${followUpTarget.name}」移出跟进清单`,
        "success",
      );
      setFollowUpTarget(null);
    } catch (err) {
      notify(err instanceof Error ? err.message : "操作失败，请稍后重试", "error");
    } finally {
      setFollowUpBusy(false);
    }
  };

  const registeredCount = countFor("registered");
  const guestCount = countFor("guest");

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        {[0, 1, 2].map((item) => (
          <div key={item} className="rounded-lg border border-background-200 bg-background-50 p-5">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-4 h-4 w-2/3" />
            <Skeleton className="mt-4 h-9 w-40" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
          <i className="ri-error-warning-line text-xl" />
        </span>
        <p className="text-sm text-foreground-600">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          重新加载
        </button>
      </div>
    );
  }

  return (
    <div id="customer-list" className="scroll-mt-24">
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line" />
          </span>
          <input
            type="text"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索客户名称、邮箱或公司"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword("")}
              aria-label="清除搜索"
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((item) => {
            const active = item.key === filter;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleFilterChange(item.key)}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
                <span className={active ? "text-background-200" : "text-foreground-400"}>{countFor(item.key)}</span>
              </button>
            );
          })}
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-secondary-700">
              <i className="ri-sort-desc" />
            </span>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as SortKey)}
              aria-label="客户排序方式"
              className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-8 pr-8 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
            >
              {SORTS.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-arrow-down-s-line" />
            </span>
          </div>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-primary-700">
              <i className="ri-user-star-line" />
            </span>
            <select
              value={assignee}
              onChange={(event) => handleAssigneeChange(event.target.value)}
              aria-label="按跟进负责人筛选"
              className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-8 pr-8 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
            >
              <option value="all">全部负责人</option>
              <option value="none">未分配负责人</option>
              {assigneeOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-arrow-down-s-line" />
            </span>
          </div>
          <button
            type="button"
            disabled={riskyCustomers.length === 0}
            onClick={handleRiskExport}
            title={
              riskyCustomers.length === 0
                ? "当前没有风险客户"
                : `导出 ${riskyCustomers.length} 位风险客户，供客服定向跟进`
            }
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3 py-2 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-700">
              <i className={riskExported ? "ri-check-line" : "ri-alarm-warning-line"} />
            </span>
            {riskExported ? "已导出" : `导出风险名单${riskyCustomers.length > 0 ? `（${riskyCustomers.length}）` : ""}`}
          </button>
          <button
            type="button"
            disabled={followedCustomers.length === 0}
            onClick={handleFollowUpExport}
            title={
              followedCustomers.length === 0
                ? "当前没有跟进中的客户"
                : `导出 ${followedCustomers.length} 位跟进中客户，方便客服看整体跟进进度`
            }
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-2 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
              <i className={followUpExported ? "ri-check-line" : "ri-flag-2-line"} />
            </span>
            {followUpExported ? "已导出" : `导出跟进清单${followedCustomers.length > 0 ? `（${followedCustomers.length}）` : ""}`}
          </button>
          <button
            type="button"
            disabled={filtered.length === 0}
            onClick={handleExport}
            title="导出台账（含名下许可证汇总）"
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
              <i className={exported ? "ri-check-line" : "ri-download-2-line"} />
            </span>
            {exported ? "已导出" : "导出客户台账"}
          </button>
        </div>
      </div>

      <p className="mb-4 text-xs text-foreground-500">
        共 <strong className="text-foreground-800">{customers.length}</strong> 位客户 · 已注册 {registeredCount} 位 ·
        游客申请 {guestCount} 位 · 风险客户{" "}
        <strong className={countFor("risky") > 0 ? "text-accent-700" : "text-foreground-800"}>
          {countFor("risky")}
        </strong>{" "}
        位 · 跟进中{" "}
        <strong className={countFor("following") > 0 ? "text-primary-700" : "text-foreground-800"}>
          {countFor("following")}
        </strong>{" "}
        位
      </p>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-group-line text-2xl" />
          </span>
          <p className="text-sm font-medium text-foreground-800">没有匹配的客户</p>
          <p className="max-w-sm text-xs text-foreground-500">调整筛选条件或搜索关键词再试试。</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {pagedItems.map((customer) => {
            const account = accountMeta(customer);
            const isOpen = expanded === customer.key;
            const initial = customer.name.slice(0, 1).toUpperCase();
            const canManage = Boolean(customer.userId) && customer.role !== "admin";
            // 该客户最近一次被单独补推续期提醒的时间（30 天内即高亮标记）。
            const singlePushAt = customer.userId ? lastSinglePushAt[customer.userId] : null;
            const isRecentSinglePush = singlePushAt
              ? Date.now() - new Date(singlePushAt).getTime() <= SINGLE_PUSH_RECENT_DAYS * 86400000
              : false;

            return (
              <article
                key={customer.key}
                className={`rounded-lg border bg-background-50 p-5 transition-colors ${
                  isOpen ? "border-primary-300" : "border-background-200 hover:border-primary-300"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                      {initial}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-heading text-base font-semibold text-foreground-950">{customer.name}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${account.className}`}>
                          {account.label}
                        </span>
                        {customer.role === "admin" && (
                          <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900">
                            管理员
                          </span>
                        )}
                        {customer.riskFlags.map((flag) => (
                          <span
                            key={flag.key}
                            title={flag.detail}
                            className={`inline-flex cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              flag.tone === "accent"
                                ? "bg-accent-100 text-accent-800"
                                : "bg-secondary-100 text-secondary-900"
                            }`}
                          >
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className={flag.key === "inactive" ? "ri-zzz-line" : "ri-swap-box-line"} />
                            </span>
                            {flag.label}
                          </span>
                        ))}
                        {customer.followUp && (
                          <span
                            title={customer.followUpNote ? `跟进备注：${customer.followUpNote}` : "已加入客服跟进清单"}
                            className="inline-flex cursor-help items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700"
                          >
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className="ri-flag-2-line" />
                            </span>
                            跟进中
                          </span>
                        )}
                        {customer.followUp && customer.followUpAssignee && (
                          <span
                            title={`跟进负责人：${customer.followUpAssignee}`}
                            className="inline-flex cursor-help items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900"
                          >
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className="ri-user-star-line" />
                            </span>
                            {customer.followUpAssignee}
                          </span>
                        )}
                        {isRecentSinglePush && singlePushAt && (
                          <span
                            title={`最近一次单独补推：${formatRelativeTime(singlePushAt)}· 可在客户健康度看板的「最近推送记录」查看详情`}
                            className="inline-flex cursor-help items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900"
                          >
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className="ri-send-plane-line" />
                            </span>
                            最近被单独补推过 · {formatRelativeTime(singlePushAt)}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 truncate text-xs text-foreground-600">{customer.email || "无邮箱"}</p>
                      {customer.company && <p className="mt-0.5 truncate text-xs text-foreground-500">{customer.company}</p>}
                      <p className="mt-1 flex items-center gap-1.5 text-[11px] text-foreground-500">
                        <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-400">
                          <i className="ri-history-line" />
                        </span>
                        最近操作{" "}
                        {customer.lastActivity ? formatRelativeTime(new Date(customer.lastActivity)) : "暂无记录"}
                        {customer.devices.length > 0 && (
                          <span className="text-foreground-400">· 设备 {customer.devices.length} 台</span>
                        )}
                      </p>
                      {customer.followUp && customer.followUpNote && (
                        <p className="mt-1 flex items-start gap-1.5 rounded-md bg-primary-50 px-2 py-1 text-[11px] text-primary-700">
                          <span className="mt-0.5 flex h-3 w-3 items-center justify-center">
                            <i className="ri-chat-1-line" />
                          </span>
                          <span className="flex-1">跟进备注：{customer.followUpNote}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : customer.key)}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                      </span>
                      {isOpen ? "收起明细" : "查看明细"}
                    </button>
                    {customer.riskFlags.length > 0 && customer.userId && (
                      <button
                        type="button"
                        onClick={() => setFollowUpTarget(customer)}
                        title={customer.followUp ? "已在跟进清单，点击编辑备注" : "加入客服跟进清单并记录备注"}
                        className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                          customer.followUp
                            ? "border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100"
                            : "border-accent-300 bg-accent-50 text-accent-800 hover:bg-accent-100"
                        }`}
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className={customer.followUp ? "ri-flag-2-fill" : "ri-flag-2-line"} />
                        </span>
                        {customer.followUp ? "跟进中" : "加入跟进清单"}
                      </button>
                    )}
                    {canManage ? (
                      <button
                        type="button"
                        onClick={() => setTarget(customer)}
                        className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                          customer.status === "suspended"
                            ? "border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100"
                            : "border-accent-300 bg-accent-50 text-accent-800 hover:bg-accent-100"
                        }`}
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className={customer.status === "suspended" ? "ri-user-follow-line" : "ri-user-forbid-line"} />
                        </span>
                        {customer.status === "suspended" ? "恢复账号" : "停用账号"}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-background-100 px-3 py-1.5 text-xs text-foreground-500">
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-lock-line" />
                        </span>
                        {customer.role === "admin" ? "管理员不可停用" : "无账号，不可停用"}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3 sm:grid-cols-4">
                  <div>
                    <p className="text-[11px] text-foreground-500">申请总数</p>
                    <p className="mt-0.5 font-heading text-lg font-semibold text-foreground-950">
                      {customer.applications.length}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] text-foreground-500">待审批</p>
                    <p className="mt-0.5 font-heading text-lg font-semibold text-foreground-950">{customer.pendingCount}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-foreground-500">许可证</p>
                    <p className="mt-0.5 font-heading text-lg font-semibold text-foreground-950">
                      {customer.licenses.length}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] text-foreground-500">授权席位</p>
                    <p className="mt-0.5 font-heading text-lg font-semibold text-foreground-950">{customer.totalSeats}</p>
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <div className="rounded-md border border-background-200 bg-background-50 p-4">
                      <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                        <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                          <i className="ri-file-list-3-line" />
                        </span>
                        授权申请（{customer.applications.length}）
                      </p>
                      {customer.applications.length === 0 ? (
                        <p className="mt-3 text-xs text-foreground-500">该客户暂无授权申请记录。</p>
                      ) : (
                        <ul className="mt-3 flex flex-col gap-2">
                          {customer.applications.map((app) => {
                            const meta =
                              APPLICATION_STATUS_META[app.status] ?? {
                                label: app.status,
                                className: "bg-background-200 text-foreground-700",
                              };
                            return (
                              <li
                                key={app.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
                              >
                                <div className="min-w-0">
                                  <p className="flex flex-wrap items-center gap-2 text-xs font-medium text-foreground-900">
                                    {app.product_name}
                                    {app.isRenewal && (
                                      <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-medium text-accent-800">
                                        续期
                                      </span>
                                    )}
                                  </p>
                                  <p className="mt-0.5 text-[10px] text-foreground-500">
                                    {editionLabel(app.edition)} · {app.seat_count} 席位 · {formatFullDate(app.created_at)}
                                  </p>
                                </div>
                                <StatusBadge meta={meta} />
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    <div className="rounded-md border border-background-200 bg-background-50 p-4">
                      <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                        <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                          <i className="ri-key-2-line" />
                        </span>
                        许可证（{customer.licenses.length}）
                      </p>
                      {customer.licenses.length === 0 ? (
                        <p className="mt-3 text-xs text-foreground-500">该客户名下暂无许可证。</p>
                      ) : (
                        <ul className="mt-3 flex flex-col gap-2">
                          {customer.licenses.map((license) => {
                            const meta =
                              LICENSE_STATUS_META[license.status] ?? {
                                label: license.status,
                                className: "bg-background-200 text-foreground-700",
                              };
                            return (
                              <li
                                key={license.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
                              >
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-foreground-900">{license.product_name}</p>
                                  <p className="mt-0.5 text-[10px] text-foreground-500">
                                    {editionLabel(license.edition)} · 激活 {license.activation_count}/{license.max_activations}{" "}
                                    · 到期 {license.expires_at ? formatFullDate(license.expires_at) : "永久"}
                                  </p>
                                </div>
                                <StatusBadge meta={meta} />
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onChange={setPage}
        itemLabel="位客户"
      />

      <CustomerStatusModal
        open={Boolean(target)}
        customer={target}
        busy={busy}
        onConfirm={() => {
          void confirmStatus();
        }}
        onCancel={() => {
          if (!busy) setTarget(null);
        }}
      />

      <FollowUpModal
        open={Boolean(followUpTarget)}
        customer={followUpTarget}
        staff={staff}
        assigneeWorkload={assigneeWorkload}
        busy={followUpBusy}
        onSave={(followUp, note, assigneeValue) => {
          void handleFollowUpSave(followUp, note, assigneeValue);
        }}
        onCancel={() => {
          if (!followUpBusy) setFollowUpTarget(null);
        }}
      />
    </div>
  );
}